import type { Scenario } from './types'

// Fictional illustrative case. Authored content — not derived from any real user.
// PCOS-related anovulatory infertility — insulin resistance + LH excess + MTHFR, framed
// as understanding why ovulation-induction isn't working and what to adjust.

export const pcosFertilityAda: Scenario = {
  slug: 'pcos-fertility-amara',
  series: 'Hormones & Mood',
  title: 'She ovulated on paper. She still wasn\'t getting pregnant.',
  socialHook:
    "Ada, 40, had PCOS, a failed Clomid round, and labs that everyone said were improving. Her genetics helped explain why her ovaries responded differently — and gave her better questions for the next treatment conversation.",
  meta: {
    description:
      "A fictional case study: Ada has PCOS-related anovulatory infertility. After one failed Clomid cycle and a borderline letrozole response, her genetics point to LH hypersensitivity, impaired insulin signaling, and a methylation gap — each one a lever her treatment plan hadn't addressed.",
  },

  persona: {
    name: 'Ada',
    age: 40,
    sex: 'Female',
    ancestry: 'Nigerian British',
    occupation: 'Secondary school teacher',
    chiefConcern:
      "Ada was diagnosed with PCOS two years ago after 14 months of trying to conceive with no success. She had irregular cycles ranging from 45 to 90 days and rarely ovulated without medication. One round of Clomid produced a large lead follicle but no confirmed pregnancy. She switched to letrozole, produced two follicles, but the cycle ended with a chemical pregnancy at 5 weeks. Her fertility specialist says her labs are 'going in the right direction.' Ada wants to understand why her ovaries respond unevenly and what her genetics might add to the conversation.",
    familyHistory:
      'Mother: PCOS, conceived both children with fertility treatment. Maternal grandmother: type 2 diabetes at 55. No known miscarriage history on either side.',
  },

  clinicalPicture: {
    symptoms: [
      'Cycles ranging 45–90 days, rarely ovulates without ovulation induction',
      'Moderate acne on jaw and upper back',
      'Mild hirsutism — upper lip and chin',
      'One chemical pregnancy after letrozole cycle',
      'Weight stable, BMI 27.2',
    ],
    labs: [
      { name: 'LH', value: '18.2 IU/L', refRange: '2–15 IU/L (follicular)', flag: 'high' },
      { name: 'FSH', value: '5.8 IU/L', refRange: '3–10 IU/L', flag: 'normal' },
      { name: 'LH:FSH ratio', value: '3.1', refRange: '<2 preferred', flag: 'high' },
      { name: 'Fasting Insulin', value: '19 µIU/mL', refRange: '<10 µIU/mL', flag: 'high' },
      { name: 'HOMA-IR', value: '4.1', refRange: '<2.5', flag: 'high' },
      { name: 'AMH', value: '8.7 ng/mL', refRange: '1.0–4.0 ng/mL typical', flag: 'high' },
      { name: 'Homocysteine', value: '13.4 µmol/L', refRange: '<10 µmol/L optimal', flag: 'high' },
      { name: 'Vitamin B12', value: '298 pg/mL', refRange: '>400 pg/mL optimal', flag: 'low' },
    ],
    medications: [
      'Letrozole 2.5 mg (days 3–7) — current cycle',
      'Prenatal vitamin with folic acid',
    ],
    supplements: ['Myo-inositol 2g/day (started 3 months ago)'],
    lifestyle: [
      'BMI 27.2, walks daily but no structured exercise',
      'Diet: home-cooked most days, occasional high-carbohydrate comfort eating around cycle stress',
      'Non-smoker, no alcohol while trying to conceive',
      'Sleep: 7–8 hours, disrupted in the week before a delayed period',
    ],
  },

  genetics: [
    {
      gene: 'LHCGR',
      variant: 'LH receptor sensitivity variant',
      rsid: 'rs2293275',
      genotype: 'Increased LH receptor sensitivity pattern',
      plainMeaning:
        "Her ovaries may over-respond to the LH surge. That can push follicles to luteinize — mature prematurely — before the egg is ready, which helps explain the uneven response to ovulation-induction cycles.",
      technicalDetail:
        "LHCGR encodes the luteinizing hormone/choriogonadotropin receptor on ovarian granulosa and theca cells. Gain-of-function variants or sensitivity-increasing polymorphisms can make ovarian cells more reactive to endogenous LH. In the context of PCOS — where baseline LH is already elevated and the LH:FSH ratio is skewed — heightened receptor sensitivity can cause premature luteinization of dominant follicles, poor oocyte quality, and early corpus luteum dysfunction. This is a plausible mechanism for a cycle that shows a large follicle on ultrasound but doesn't produce a viable pregnancy.",
    },
    {
      gene: 'INSR',
      variant: 'Insulin receptor signaling variant',
      rsid: 'rs2059806',
      genotype: 'Reduced insulin signaling efficiency',
      plainMeaning:
        "Her cells require more insulin to move glucose effectively, which drives extra androgen production in the ovary and suppresses the proteins that would keep testosterone bound and inactive.",
      technicalDetail:
        "Reduced INSR signaling efficiency means the pancreas compensates with higher insulin output. In the ovary, hyperinsulinemia directly stimulates theca cell androgen production and suppresses hepatic SHBG synthesis, raising free androgen levels. The consequence in PCOS is a self-amplifying loop: more androgen further disrupts follicle maturation, delays or prevents normal ovulation, and raises LH relative to FSH. Myo-inositol supplementation targets this pathway by improving insulin signal transduction in ovarian cells — Ada's recent inositol start is directionally correct.",
    },
    {
      gene: 'MTHFR',
      variant: 'C677T',
      rsid: 'rs1801133',
      genotype: 'C/T — one copy, moderately reduced folate activation',
      plainMeaning:
        "Her prenatal folic acid may not fully convert to the active form her body needs, particularly with B12 already running low. Elevated homocysteine supports this bottleneck.",
      technicalDetail:
        "MTHFR C677T heterozygosity reduces conversion of folic acid to 5-methyltetrahydrofolate (5-MTHF) by roughly 35%. Combined with low B12 — a required cofactor in homocysteine remethylation — this can elevate homocysteine above the fertility-relevant threshold. Elevated homocysteine has been associated with impaired endometrial receptivity and early pregnancy loss in several observational studies. Ada's chemical pregnancy may reflect implantation-environment issues in addition to oocyte quality concerns. Switching to methylfolate and correcting B12 is a practical, low-risk intervention.",
    },
    {
      gene: 'DENND1A',
      variant: 'PCOS ovarian androgen variant',
      rsid: 'rs10818854',
      genotype: 'Risk allele carrier',
      plainMeaning:
        "Her ovaries have a genetic tendency to amplify androgen production when insulin and LH signals rise — which in her case, both do. This is likely part of why her PCOS presents with strong androgen symptoms alongside the ovulation problem.",
      technicalDetail:
        "DENND1A is one of the most consistently replicated PCOS susceptibility loci in genome-wide association studies. DENND1A.V2 overexpression in theca cells increases androgen biosynthesis gene expression. The pathway is sensitive to both LH and insulin stimulation, which means Ada's elevated LH, elevated fasting insulin, and DENND1A risk allele may be acting synergistically to maintain a high-androgen ovarian environment that impairs follicle maturation even on ovulation-induction.",
    },
  ],

  beats: [
    {
      type: 'narrative',
      heading: 'The follicles were there. The pregnancies weren\'t.',
      body: "Ada did everything right by the standard checklist. She took her medication on the right days, came in for her monitoring scans, and watched the follicle grow on the ultrasound screen. She left feeling hopeful. The Clomid cycle produced nothing. The letrozole cycle produced a positive test that disappeared at five weeks. Her numbers were trending better, she was told. The plan was to try again. What she wanted was a better explanation for why the follicles that appeared on screen were not becoming the pregnancies she expected.",
    },
    {
      type: 'narrative',
      heading: 'PCOS infertility is not one problem',
      body: "PCOS-related infertility often gets simplified into 'she doesn't ovulate,' which is true but incomplete. The mechanisms that disrupt ovulation also affect egg quality, the hormonal environment around the follicle, the lining that receives a fertilized egg, and the early signaling of a new pregnancy. Ada's PCOS isn't just an ovulation problem. It's an LH problem, an insulin problem, a methylation problem, and possibly an early luteal phase problem — each one tugging at the others.",
    },
    {
      type: 'reveal',
      heading: 'The LH:FSH ratio was the clue the ultrasound confirmed',
      body: "Ada's LH was running more than three times her FSH — a ratio that reflects the hormonal imbalance underlying PCOS. High LH tells the ovaries to make more androgen and pushes the final maturation signal earlier than it should arrive. If her LHCGR receptor is also more sensitive to that already-elevated LH signal, the follicle can luteinize before the egg inside is ready to fertilize or implant. A large follicle on scan can look like success while the biology underneath has already moved past the window.",
    },
    {
      type: 'reveal',
      heading: 'The chemical pregnancy pointed to the lining, not just the egg',
      body: "A chemical pregnancy — a positive test that disappears before ultrasound confirmation — can mean the egg fertilized but the embryo couldn't implant or sustain itself. Ada's homocysteine is elevated, her B12 is suboptimal, and her MTHFR C/T result means folic acid conversion may be incomplete. Homocysteine has a direct effect on the endometrial environment in early implantation studies. Her prenatal vitamin contains folic acid, not methylfolate. The chemical pregnancy may be telling a story about the lining that the Clomid cycle — which ended without a pregnancy at all — never had the chance to tell.",
    },
    {
      type: 'action',
      heading: 'Five conversations to have before the next cycle',
      items: [
        'Ask the fertility specialist to review the LH:FSH ratio and discuss whether LH suppression — a GnRH agonist protocol, or monitoring for premature LH surge during the next stimulation — would reduce premature luteinization risk.',
        'Switch from folic acid to methylfolate (400–800 µg 5-MTHF) and add a B12 supplement. Then recheck homocysteine before the next medicated cycle. The goal is to normalize the lining environment before another transfer attempt.',
        'Review the inositol dose and form with the specialist. Myo-inositol at 4g/day with 400 µg D-chiro-inositol is the more studied ratio for PCOS insulin signaling. Three months of consistent use tends to show measurable LH and insulin changes.',
        'Add two to three resistance training sessions per week. Muscle is the primary glucose disposal site after meals; building muscle insulin sensitivity directly reduces the fasting insulin that drives androgen and LH excess.',
        'Ask about a progesterone check at 7 days post-ovulation on the next cycle to assess whether the luteal phase is adequate. LH hypersensitivity can cause luteal phase deficiency even when ovulation occurs.',
      ],
    },
  ],

  extraBeats: [
    {
      type: 'streak' as const,
      heading: "Ada's pre-cycle preparation goal",
      goalLabel: 'Methylfolate + B12 + inositol protocol before next monitoring scan',
      caption:
        'Ada used the gap before her next scheduled cycle to address the modifiable methylation and insulin findings. Her goal was to arrive at the next scan with homocysteine trending down and a fuller pre-cycle baseline.',
      weeks: [
        {
          weekLabel: 'Week 1',
          days: [true, true, true, false, true, true, false],
          note: 'Switched to methylfolate prenatal. Added B12 1000 µg sublingual. Inositol dose increased.',
        },
        {
          weekLabel: 'Week 2',
          days: [true, true, true, true, true, false, true],
          note: 'First resistance training session. Felt manageable. Sleep improving slightly.',
        },
        {
          weekLabel: 'Week 3',
          days: [true, true, true, true, true, true, false],
          note: 'Homocysteine recheck booked. Wrote down questions for fertility specialist.',
        },
        {
          weekLabel: 'Week 4',
          days: [true, true, true, true, true, true, true],
          note: 'First fully consistent week. Protocol feels like something she controls, not just waits through.',
        },
      ],
    },
  ],

  cta: {
    namedPrompt: 'pcos-fertility-amara',
    label: 'Explore your PCOS and fertility genetics in Coach',
  },
}
